import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  GitBranch,
  AlertTriangle,
  Key,
  FileWarning,
  ExternalLink,
  Shield,
  Star,
  GitFork,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

// Secret type mapping
type SecretType =
  | "aws_access_key"
  | "aws_secret_key"
  | "private_key"
  | "api_key"
  | "database_url"
  | "generic_password"
  | "jwt_secret"
  | "slack_token"
  | "stripe_key"
  | "twilio_key"
  | "sendgrid_key"
  | "heroku_api_key"
  | "google_api_key"
  | "github_token"
  | "internal_reference"
  | "azure_key"
  | "firebase_key"
  | "npm_token"
  | "pypi_token"
  | "docker_credentials";

type Severity = "critical" | "high" | "medium" | "low";

interface LeakedSecret {
  secret_type: SecretType;
  severity: Severity;
  file_path: string;
  line_number?: number;
  snippet: string;
  repository: string;
  repository_url: string;
  owner: string;
  match_pattern: string;
  discovered_at: string;
}

interface RepositoryInfo {
  name: string;
  full_name: string;
  url: string;
  description?: string;
  owner: string;
  is_fork: boolean;
  stars: number;
  last_updated: string;
  secrets_found: number;
}

export interface ReposModuleData {
  score?: number;
  target?: string;
  search_queries_used?: string[];
  repositories_scanned?: number;
  repositories?: RepositoryInfo[];
  secrets?: LeakedSecret[];
  issues?: Array<string | Issue>;
}

export interface ReposModuleViewProps {
  data: ReposModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Severity config
const SEVERITY_CONFIG: Record<Severity, { label: string; color: string; badgeColor: string }> = {
  critical: {
    label: "Critical",
    color: "text-red-400",
    badgeColor: "bg-red-500/15 text-red-400 border-red-500/40",
  },
  high: {
    label: "High",
    color: "text-orange-400",
    badgeColor: "bg-orange-500/15 text-orange-400 border-orange-500/40",
  },
  medium: {
    label: "Medium",
    color: "text-amber-400",
    badgeColor: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  },
  low: {
    label: "Low",
    color: "text-cyan-400",
    badgeColor: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40",
  },
};

// Secret type labels
const SECRET_TYPE_LABELS: Record<SecretType, string> = {
  aws_access_key: "AWS Access Key",
  aws_secret_key: "AWS Secret Key",
  private_key: "Private Key",
  api_key: "API Key",
  database_url: "Database URL",
  generic_password: "Password",
  jwt_secret: "JWT Secret",
  slack_token: "Slack Token",
  stripe_key: "Stripe Key",
  twilio_key: "Twilio Key",
  sendgrid_key: "SendGrid Key",
  heroku_api_key: "Heroku API Key",
  google_api_key: "Google API Key",
  github_token: "GitHub Token",
  internal_reference: "Internal Reference",
  azure_key: "Azure Key",
  firebase_key: "Firebase Key",
  npm_token: "NPM Token",
  pypi_token: "PyPI Token",
  docker_credentials: "Docker Credentials",
};

// Remediation guidance per secret type
const REMEDIATION_GUIDANCE: Record<string, { steps: string[]; link?: string }> = {
  aws_access_key: {
    steps: [
      "Immediately deactivate the compromised key in AWS IAM console",
      "Create a new access key pair",
      "Update all services using the old key",
      "Review CloudTrail logs for unauthorized access",
    ],
    link: "https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html",
  },
  aws_secret_key: {
    steps: [
      "Rotate the AWS access key pair immediately",
      "Delete the compromised key from IAM",
      "Review S3 and other service access logs",
    ],
    link: "https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html",
  },
  private_key: {
    steps: [
      "Revoke the private key certificate",
      "Generate a new key pair",
      "Update all systems using the key",
      "Check for unauthorized access using the compromised key",
    ],
  },
  github_token: {
    steps: [
      "Revoke the token in GitHub Settings > Developer Settings",
      "Generate a new token with minimal required scopes",
      "Use GitHub's secret scanning alerts",
    ],
    link: "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens",
  },
  stripe_key: {
    steps: [
      "Roll the API key in Stripe Dashboard",
      "Check for unauthorized transactions",
      "Enable webhook signatures for additional security",
    ],
    link: "https://stripe.com/docs/keys",
  },
  database_url: {
    steps: [
      "Change the database password immediately",
      "Review database access logs",
      "Consider IP whitelisting for database access",
      "Enable SSL/TLS for database connections",
    ],
  },
  jwt_secret: {
    steps: [
      "Rotate the JWT secret key",
      "Invalidate all existing tokens",
      "Consider implementing token blacklisting",
    ],
  },
  slack_token: {
    steps: [
      "Revoke the token in Slack App settings",
      "Generate a new token",
      "Review Slack audit logs for unauthorized messages",
    ],
  },
  generic_password: {
    steps: [
      "Change the password immediately",
      "Enable multi-factor authentication if available",
      "Review access logs for the affected service",
    ],
  },
  api_key: {
    steps: [
      "Rotate the API key in the service provider's dashboard",
      "Update all applications using the key",
      "Review API usage logs for anomalies",
    ],
  },
};

// Pie chart colors
const PIE_COLORS = [
  "#f87171", // red-400
  "#fb923c", // orange-400
  "#fbbf24", // amber-400
  "#22d3ee", // cyan-400
  "#818cf8", // indigo-400
  "#34d399", // emerald-400
  "#f472b6", // pink-400
  "#a78bfa", // violet-400
];

// Export secrets as redacted CSV
function exportRedactedCsv(secrets: LeakedSecret[], target?: string) {
  const maskSecret = (snippet: string) => {
    // Mask everything except first 4 and last 4 characters
    if (snippet.length <= 12) return "****";
    return snippet.slice(0, 4) + "****" + snippet.slice(-4);
  };

  const headers = ["Severity", "Type", "Repository", "File Path", "Line", "Snippet (Redacted)", "Discovered At"];
  const csvContent = [
    headers.join(","),
    ...secrets.map((s) => [
      s.severity,
      `"${SECRET_TYPE_LABELS[s.secret_type] || s.secret_type}"`,
      `"${s.repository}"`,
      `"${s.file_path}"`,
      s.line_number || "",
      `"${maskSecret(s.snippet)}"`,
      `"${s.discovered_at}"`,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `secrets_report_${target || "scan"}_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Secret Type Distribution Chart
function SecretTypeChart({ secrets }: { secrets: LeakedSecret[] }) {
  const chartData = React.useMemo(() => {
    const counts: Record<string, number> = {};
    secrets.forEach((s) => {
      const label = SECRET_TYPE_LABELS[s.secret_type] || s.secret_type;
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [secrets]);

  if (chartData.length === 0) return null;

  return (
    <div className="h-[220px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={75}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "0.5rem",
            }}
            itemStyle={{ color: "hsl(var(--foreground))" }}
          />
          <Legend
            wrapperStyle={{ fontSize: "11px" }}
            formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// Remediation Card Component
function RemediationCard({ secretType }: { secretType: SecretType }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const guidance = REMEDIATION_GUIDANCE[secretType] || REMEDIATION_GUIDANCE.api_key;
  const label = SECRET_TYPE_LABELS[secretType] || secretType;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-cyan-400" />
          <span className="font-medium text-sm">{label} - Rotation Steps</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isExpanded && (
        <div className="p-4 border-t border-border space-y-3">
          <ol className="list-decimal list-inside space-y-2">
            {guidance.steps.map((step, index) => (
              <li key={index} className="text-sm text-muted-foreground">{step}</li>
            ))}
          </ol>
          {guidance.link && (
            <a
              href={guidance.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
            >
              <Info className="h-3.5 w-3.5" />
              Documentation
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "high";
      if (issue.toLowerCase().includes("critical") || issue.toLowerCase().includes("private key")) {
        severity = "critical";
      } else if (issue.toLowerCase().includes("api key") || issue.toLowerCase().includes("token")) {
        severity = "high";
      }
      return { id: `repos-issue-${index}`, severity, message: issue, module: "repos" };
    }
    return issue;
  });
}

export function ReposModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: ReposModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const secrets = data.secrets || [];
  const repositories = data.repositories || [];

  // Count by severity
  const severityCounts = secrets.reduce((acc, secret) => {
    acc[secret.severity] = (acc[secret.severity] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Repository columns
  const repoColumns: ColumnDef<RepositoryInfo>[] = [
    {
      id: "name",
      header: "Repository",
      accessor: (row) => row.full_name,
      cell: (value, row) => (
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-muted-foreground" />
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm hover:text-cyan-400"
          >
            {value as string}
          </a>
          {row.is_fork && (
            <Badge variant="outline" className="text-xs">
              <GitFork className="h-3 w-3 mr-1" />
              Fork
            </Badge>
          )}
        </div>
      ),
      sortable: true,
      filterable: true,
    },
    {
      id: "stars",
      header: "Stars",
      accessor: (row) => row.stars,
      cell: (value) => (
        <div className="flex items-center gap-1 text-muted-foreground">
          <Star className="h-3 w-3" />
          <span className="font-mono text-sm">{(value as number).toLocaleString()}</span>
        </div>
      ),
      sortable: true,
      align: "right",
      width: "100px",
    },
    {
      id: "secrets_found",
      header: "Secrets",
      accessor: (row) => row.secrets_found,
      cell: (value) => (
        <span className={cn("font-mono text-sm font-semibold", (value as number) > 0 ? "text-red-400" : "text-emerald-400")}>
          {value as number}
        </span>
      ),
      sortable: true,
      align: "right",
      width: "100px",
    },
  ];

  // Secrets columns
  const secretColumns: ColumnDef<LeakedSecret>[] = [
    {
      id: "severity",
      header: "Severity",
      accessor: (row) => row.severity,
      cell: (value) => {
        const config = SEVERITY_CONFIG[value as Severity];
        return (
          <Badge variant="outline" className={cn("text-xs", config.badgeColor)}>
            {config.label}
          </Badge>
        );
      },
      sortable: true,
      width: "100px",
    },
    {
      id: "secret_type",
      header: "Type",
      accessor: (row) => row.secret_type,
      cell: (value) => (
        <div className="flex items-center gap-2">
          <Key className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">{SECRET_TYPE_LABELS[value as SecretType] || String(value)}</span>
        </div>
      ),
      sortable: true,
      filterable: true,
    },
    {
      id: "file_path",
      header: "Location",
      accessor: (row) => row.file_path,
      cell: (value, row) => (
        <div>
          <code className="text-xs font-mono bg-muted/50 px-1 py-0.5 rounded">
            {value as string}
            {row.line_number && `:${row.line_number}`}
          </code>
        </div>
      ),
      sortable: true,
    },
    {
      id: "repository",
      header: "Repository",
      accessor: (row) => row.repository,
      cell: (value, row) => (
        <a
          href={row.repository_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm hover:text-cyan-400 flex items-center gap-1"
        >
          {value as string}
          <ExternalLink className="h-3 w-3" />
        </a>
      ),
      sortable: true,
      filterable: true,
    },
  ];

  return (
    <ModuleContainer
      moduleName="repos"
      icon={GitBranch}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className={cn("bg-card/50", (severityCounts.critical || 0) > 0 && "border-red-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Shield className="h-3 w-3 text-red-400" /> Critical
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", (severityCounts.critical || 0) > 0 ? "text-red-400" : "text-emerald-400")}>
                {severityCounts.critical || 0}
              </p>
            </CardContent>
          </Card>
          <Card className={cn("bg-card/50", (severityCounts.high || 0) > 0 && "border-orange-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-orange-400" /> High
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", (severityCounts.high || 0) > 0 ? "text-orange-400" : "text-emerald-400")}>
                {severityCounts.high || 0}
              </p>
            </CardContent>
          </Card>
          <Card className={cn("bg-card/50", (severityCounts.medium || 0) > 0 && "border-amber-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-400" /> Medium
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", (severityCounts.medium || 0) > 0 ? "text-amber-400" : "text-emerald-400")}>
                {severityCounts.medium || 0}
              </p>
            </CardContent>
          </Card>
          <Card className={cn("bg-card/50", (severityCounts.low || 0) > 0 && "border-cyan-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Info className="h-3 w-3 text-cyan-400" /> Low
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", (severityCounts.low || 0) > 0 ? "text-cyan-400" : "text-emerald-400")}>
                {severityCounts.low || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Repos Scanned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{data.repositories_scanned || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Secrets Found
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", secrets.length > 0 ? "text-red-400" : "text-emerald-400")}>
                {secrets.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Export Button */}
        {secrets.length > 0 && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportRedactedCsv(secrets, data.target)}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export Redacted Report
            </Button>
          </div>
        )}

        {/* Critical Alert */}
        {(severityCounts.critical || 0) > 0 && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 animate-pulse">
            <div className="flex items-center gap-3">
              <FileWarning className="h-6 w-6 text-red-400" />
              <div>
                <h4 className="font-semibold text-red-400">Critical: Leaked Secrets Detected</h4>
                <p className="text-sm text-muted-foreground">
                  {severityCounts.critical} critical secret(s) found in public repositories. Immediate action required.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Repositories Table */}
        {repositories.length > 0 && (
          <ModuleSection
            title="Repositories with Secrets"
            icon={GitBranch}
            badge={<Badge variant="outline" className="text-xs">{repositories.length}</Badge>}
          >
            <ModuleDataTable
              data={repositories}
              columns={repoColumns}
              paginated={repositories.length > 10}
              searchable
              searchPlaceholder="Search repositories..."
            />
          </ModuleSection>
        )}

        {/* Secrets Table */}
        {secrets.length > 0 && (
          <ModuleSection
            title="Leaked Secrets"
            icon={Key}
            badge={<Badge variant="outline" className="text-xs bg-red-500/10 border-red-500/30 text-red-400">{secrets.length}</Badge>}
          >
            <ModuleDataTable
              data={secrets}
              columns={secretColumns}
              paginated={secrets.length > 10}
              searchable
              searchPlaceholder="Search secrets..."
            />
          </ModuleSection>
        )}

        {/* Secret Type Distribution */}
        {secrets.length > 0 && (
          <ModuleSection
            title="Secret Type Distribution"
            icon={Key}
          >
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <SecretTypeChart secrets={secrets} />
              </div>
            </div>
          </ModuleSection>
        )}

        {/* Remediation Guidance */}
        {secrets.length > 0 && (
          <ModuleSection
            title="Remediation Guidance"
            icon={RefreshCw}
          >
            <div className="space-y-2">
              {Array.from(new Set(secrets.map((s) => s.secret_type))).map((secretType) => (
                <RemediationCard key={secretType} secretType={secretType} />
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

ReposModuleView.displayName = "ReposModuleView";
